import React, { useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Modal } from '../common/Modal';
import { getTranslation, mergeTranslations, TranslatedValue, Translations, TranslationEdit } from '../common/TranslatedValue';
import Button from '../common/Button';
import { toast } from 'react-toastify';
import { AddAdVariationType_DeleteVariationTypeMutation, AddAdVariationType_DeleteVariationTypeMutationVariables } from '../../../codegen/graphql';

const CREATE_VARIATION_TYPE = gql`
  mutation AddAdVariationType_CreateVariationType($input: CreateAdVariationTypeInput!) {
    createAdVariationType(input: $input) {
      id
      type
      typeValues {
        id
        value
      }
    }
  }
`;

const UPDATE_VARIATION_TYPE = gql`
  mutation AddAdVariationType_UpdateVariationType($id: ID!, $input: UpdateAdVariationTypeInput!) {
    updateAdVariationType(id: $id, input: $input) {
      id
      type
    }
  }
`;

export const DELETE_VARIATION_TYPE = gql`
  mutation AddAdVariationType_DeleteVariationType($id: ID!) {
    deleteAdVariationType(id: $id)
  }
`;

interface AddAdVariationTypeProps {
  isOpen: boolean;
  onClose: () => void;
  adId: string;
  typeId?: string;
  typeName?: Translations | string;
  onSuccess?: () => void;
}

export function AddAdVariationType({ 
  isOpen, 
  onClose, 
  adId, 
  typeId, 
  typeName = {}, 
  onSuccess 
}: AddAdVariationTypeProps) {
  const initialTranslations: Translations = typeof typeName === 'string'
    ? (typeName ? { FR: typeName } : {})
    : (typeName ?? {});
  const [translations, setTranslationsState] = useState<Translations>(initialTranslations);
  const translationsRef = useRef(translations);
  const setTranslations = (next: Translations) => {
    translationsRef.current = next;
    setTranslationsState(next);
  };
  const editTranslations = (updates: TranslationEdit[]) => {
    setTranslations(mergeTranslations(translationsRef.current, updates));
  };
  const [createVariationType] = useMutation(CREATE_VARIATION_TYPE);
  const [updateVariationType] = useMutation(UPDATE_VARIATION_TYPE);
  const [deleteVariationType] = useMutation<
    AddAdVariationType_DeleteVariationTypeMutation,
    AddAdVariationType_DeleteVariationTypeMutationVariables
  >(DELETE_VARIATION_TYPE);


  const isEditing = !!typeId;

  const handleSubmit = async () => {
    const currentTranslations = translationsRef.current;
    const displayName = getTranslation(currentTranslations);
    if (!displayName.trim()) {
      toast.error('Le nom du type ne peut pas être vide');
      return;
    }

    try {
      if (isEditing && typeId) {
        await updateVariationType({
          variables: {
            id: typeId,
            input: {
              type: currentTranslations,
            },
          },
        });
        toast.success(`Type "${displayName}" modifié avec succès`);
      } else {
        await createVariationType({
          variables: {
            input: {
              adId,
              type: currentTranslations,
            },
          },
        });
        toast.success(`Type "${displayName}" créé avec succès`);
      }
      
      onSuccess?.();
      onClose();
      setTranslations({});
    } catch (error) {
      console.error('Error with variation type:', error);
      toast.error('Une erreur est survenue');
    }
  };

  const handleDeleteType = async (typeId: string) => {
    if (!confirm(`Voulez-vous vraiment supprimer le type "${typeId}" ?`)) {
      return;
    }

    try {
      await deleteVariationType({ variables: { id: typeId } });
    } catch (error) {
      console.error('Error deleting variation type:', error);
    }
  };

  const handleClose = () => {
    setTranslations(initialTranslations);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Modifier le type' : 'Ajouter un type'}
      width="small"
    >
      <div className="space-y-4">
        <TranslatedValue
          translations={translations}
          renderAs="input"
          label="Nom du type"
          placeholder="Entrer le nom du type"
          autoFocus
          onEditTranslation={editTranslations}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSubmit();
            }
          }}
        />

        {typeId && <Button
          preset="danger"
          onClick={() => handleDeleteType(typeId)}
        >
          Supprimer
        </Button>}
        
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleClose}
            preset="white"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
          >
            {isEditing ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
